/**
 * Sentry SPA-side error monitoring.
 *
 * Reads DSN from VITE_SENTRY_DSN. If unset, logs a warning and no-ops so dev
 * builds and unconfigured environments don't crash on init.
 *
 * Sample-rate strategy (cost-conscious):
 *   - traces: 10% in prod, 100% in dev
 *   - replays: 0% baseline session sampling, 100% on errors only
 *   - text masked, media blocked (PII safety on session replays)
 */
import * as Sentry from '@sentry/react';

/**
 * Which deployment is this bundle actually running on?
 *
 * NOT import.meta.env.MODE: staging and production are both `vite build`, so
 * MODE is the string "production" in both, and every staging error (including
 * a full regression fleet's worth of deliberate breakage) landed in Sentry
 * tagged `environment: production`, indistinguishable from a real client's.
 * The host is the only honest signal available at runtime, so use it.
 */
function deploymentEnvironment(): string {
  if (typeof window === 'undefined') return 'unknown';
  const host = window.location.hostname;
  if (host === 'portal.adiimmigration.com.au') return 'production';
  if (host.endsWith('.vercel.app')) return 'staging';
  if (host === 'localhost' || host === '127.0.0.1') return 'development';
  return `other:${host}`;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const release = (import.meta.env.VITE_BUNDLE_ID as string | undefined) || undefined;

  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn(
      '[sentry] VITE_SENTRY_DSN is not set, error monitoring is disabled. ' +
        'Set it in the Vercel project env (prod/preview) or .env.local (dev).',
    );
    return;
  }

  const environment = deploymentEnvironment();
  const isProd = environment === 'production';

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate: isProd ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    beforeSend(event, hint) {
      // Filter ResizeObserver noise that slips past ignoreErrors
      const message =
        (event.message as string | undefined) ||
        (hint?.originalException as { message?: string } | undefined)?.message ||
        '';
      if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
        return null;
      }

      // Filter network failures with status 0 (typically ad-blockers, offline,
      // or CORS preflight failures we can't act on).
      const originalException = hint?.originalException as
        | { status?: number; statusCode?: number }
        | undefined;
      const status = originalException?.status ?? originalException?.statusCode;
      if (status === 0) {
        return null;
      }

      // Filter errors originating in browser extensions
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const hasExtensionFrame = frames.some((frame) => {
        const filename = frame.filename || '';
        return (
          filename.includes('chrome-extension://') ||
          filename.includes('moz-extension://') ||
          filename.includes('safari-extension://') ||
          filename.includes('safari-web-extension://')
        );
      });
      if (hasExtensionFrame) {
        return null;
      }

      return event;
    },
  });
}
